import React, { useEffect, useState, useRef } from "react";
import { useLocation } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import { userService } from './services/userService';
import { supabase } from './lib/supabase';
import Icon from "./components/AppIcon";
import Routes from "./Routes";

function App() {
  const location = useLocation();
  const { user } = useAuth(); // Access user from AuthContext
  const chatIframeRef = useRef(null);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [isChatMinimized, setIsChatMinimized] = useState(false);
  const [hasActiveSession, setHasActiveSession] = useState(false);

  // Check if logged-in user has an active session on load to restore their chat bubble
  useEffect(() => {
    if (!user?.id) {
      setHasActiveSession(false);
      return;
    }
    const checkActiveSession = async () => {
      try {
        const { data } = await supabase
          .from('support_messages')
          .select('session_id')
          .eq('user_id', user.id)
          .eq('is_active', true)
          .maybeSingle();
        
        if (data) {
          setHasActiveSession(true);
          setIsChatOpen(true);
          setIsChatMinimized(true); // Minimized by default so it doesn't pop up aggressively
        } else {
          setHasActiveSession(false);
        }
      } catch (e) {
        console.warn('Error checking active session:', e);
      }
    };
    checkActiveSession();
  }, [user?.id]);

  // Iframe message listener for session syncing and close events
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data && event.data.type === 'chat-ready') {
        const postSession = async () => {
            const { data: { session } } = await supabase.auth.getSession();
            if (chatIframeRef.current?.contentWindow) {
                chatIframeRef.current.contentWindow.postMessage({ type: 'supabase-session', payload: session || null }, '*');
            }
        };
        postSession();
      }
      if (event.data && event.data.type === 'close-chat') {
        setIsChatOpen(false);
        setIsChatMinimized(false);
        setHasActiveSession(false);
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  // Custom Event Listeners for opening/closing chat from SupportCenter
  useEffect(() => {
    const handleOpenChat = () => {
      setIsChatOpen(true);
      setIsChatMinimized(false);
      setHasActiveSession(true);
    };
    const handleCloseChat = () => {
      setIsChatOpen(false);
      setIsChatMinimized(false);
      setHasActiveSession(false);
    };

    window.addEventListener('open-support-chat', handleOpenChat);
    window.addEventListener('close-support-chat', handleCloseChat);

    return () => {
      window.removeEventListener('open-support-chat', handleOpenChat);
      window.removeEventListener('close-support-chat', handleCloseChat);
    };
  }, []);

  // Sync session when chat opens or user changes
  useEffect(() => {
    if (isChatOpen && chatIframeRef.current) {
      const postSession = async () => {
          const { data: { session } } = await supabase.auth.getSession();
          if (chatIframeRef.current?.contentWindow) {
              chatIframeRef.current.contentWindow.postMessage({ type: 'supabase-session', payload: session }, '*');
          }
      };
      postSession();
    }
  }, [isChatOpen, user?.id]);

  useEffect(() => {
    if (location.pathname) {
      const skipPaths = [
        '/3d-model-viewer',
        '/vehicle',
        '/product',
        '/model-catalog',
        '/user-account-dashboard',
        '/seller-dashboard'
      ];

      const shouldSkip = skipPaths.some(path => location.pathname.startsWith(path));

      if (!shouldSkip) {
        const searchParams = new URLSearchParams(location.search);
        const productId = searchParams.get('id') || searchParams.get('productId');
        const componentId = searchParams.get('componentId');
        // Record activity for all users. userService handles generating an anonymous ID if user?.id is null.
        userService.recordActivity(user?.id || null, navigator.userAgent, 'Page View', location.pathname, productId, componentId).catch(e => console.warn('Activity tracking error:', e));
      }
    }
  }, [location.pathname, location.search, user?.id]);

  return (
    <>
      <Routes />

      {/* Global Support Chat Widget */}
      {hasActiveSession && (
        <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end">
          {isChatOpen && (
            <div className={`mb-4 bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden flex flex-col relative transition-all duration-300 ${isChatMinimized ? 'w-[260px] h-[48px]' : 'w-[360px] h-[540px]'}`}>
              {/* Symmetrical Header Bar */}
              <div 
                onClick={() => isChatMinimized && setIsChatMinimized(false)}
                className={`bg-slate-800 text-white p-3 flex justify-between items-center flex-shrink-0 select-none ${isChatMinimized ? 'cursor-pointer hover:bg-slate-700' : ''}`}
              >
                <span className="font-semibold text-sm flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-emerald-500 animate-ping" />
                  Support Chat
                </span>
                <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                  <button 
                    onClick={() => setIsChatMinimized(!isChatMinimized)} 
                    className="text-white hover:text-slate-300 transition-colors p-1"
                    title={isChatMinimized ? "Expand" : "Minimize"}
                  >
                    <span className="text-xl leading-none font-bold select-none">{isChatMinimized ? "▢" : "—"}</span>
                  </button>
                  <button 
                    onClick={() => { setIsChatOpen(false); setIsChatMinimized(false); setHasActiveSession(false); }} 
                    className="text-white hover:text-red-400 transition-colors p-1"
                    title="Close"
                  >
                    <span className="text-sm font-bold select-none">✕</span>
                  </button>
                </div>
              </div>
              <div className={`flex-1 w-full h-full ${isChatMinimized ? 'hidden' : 'block'}`}>
                <iframe
                  ref={chatIframeRef}
                  src="/chat-client/"
                  className="w-full h-full border-none bg-gray-50/50"
                  title="Support Chat"
                />
              </div>
            </div>
          )}
          {!isChatOpen && (
            <button 
              onClick={() => setIsChatOpen(true)}
              className="bg-primary text-primary-foreground w-14 h-14 rounded-full flex items-center justify-center shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-200 ring-4 ring-primary/20"
            >
              <Icon name="MessageSquare" size={26} />
            </button>
          )}
        </div>
      )}
    </>
  );
}

export default App;
